import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Steps, theme, Empty, Flex, Spin, Typography } from 'antd';
import _ from 'lodash';

import GeneralHelper from '../../helpers/general';
import LocalStorageHelper from '../../helpers/local_storage';
import Confirmation from '../../components/Confirmation';
import ImportantNotesView from './components/ImportantNotesView';
import PersonalParticularsView from './components/PersonalParticularsView';
import EducationBackgroundView from './components/EducationBackgroundView';
import ApplicationInformationView from './components/ApplicationInformationView';
import SupportingDocumentsView from './components/SupportingDocumentsView';
import InformationOfRefereesView from './components/InformationOfRefereesView';
import ReviewAndSubmissionView from './components/ReviewAndSubmissionView';
import ApplicationStatusView from './components/ApplicationStatusView';

const { useToken } = theme;
const { Text } = Typography;

const ApplicationPage = (props) => {

	const [confirmation, setConfirmation] = useState(false);
	const [selectedStep, setSelectedStep] = useState(null);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	const history = useHistory();
	const { token } = useToken();
	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	const query = GeneralHelper.getParamsFromUrl();
	const {
		getApplication,
		reviewAndSubmissionPageProps,
		resetApplicationPage,
		resetApplicationStatusPage,
		resetImportantNotesPage,
		resetPersonalParticularPage,
		resetEducationBackgroundPage,
		resetApplicationInfoPage,
		resetDocumentPage,
		resetRefereeInfoPage,
		getPublishedEvent,
		loginPageProps
	} = props;
	const { application } = reviewAndSubmissionPageProps;
	const { event, loading } = loginPageProps;

	const isMobileView = useMemo(() => {
		if (windowWidth < 1175) {
			return true;
		} else {
			return false;
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [windowWidth]);

	const itemsWithTitle = [
		{
			title: 'Important Notes',
			disabled: application.status !== 'draft'
		},
		{
			title: 'Personal Particulars',
			disabled: !(application.status === 'draft' &&
					application.current_step &&
					application.current_step >= 1)
		},
		{
			title: 'Education Background',
			disabled: !(application.status === 'draft' &&
			application.current_step &&
			application.current_step >= 2)
		},
		{
			title: 'Application Information',
			disabled: !(application.status === 'draft' &&
			application.current_step &&
			application.current_step >= 3)
		},
		{
			title: 'Supporting Documents',
			disabled: !(application.status === 'draft' &&
			application.current_step &&
			application.current_step >= 4)
		},
		{
			title: 'Information of Referees',
			disabled: !(application.status === 'draft' &&
			application.current_step &&
			application.current_step >= 5)
		},
		{
			title: 'Review & Submission',
			disabled: !(application.status === 'draft' &&
			application.current_step &&
			application.current_step >= 6)
		},
		{
			title: 'Application Status',
			disabled: true
		}
	];
	const itemsWithOutTitle = [
		{
			title: '',
			disabled: application.status !== 'draft'
		},
		{
			title: '',
			disabled: !(application.status === 'draft' &&
					application.current_step &&
					application.current_step >= 1)
		},
		{
			title: '',
			disabled: !(application.status === 'draft' &&
			application.current_step &&
			application.current_step >= 2)
		},
		{
			title: '',
			disabled: !(application.status === 'draft' &&
			application.current_step &&
			application.current_step >= 3)
		},
		{
			title: '',
			disabled: !(application.status === 'draft' &&
			application.current_step &&
			application.current_step >= 4)
		},
		{
			title: '',
			disabled: !(application.status === 'draft' &&
			application.current_step &&
			application.current_step >= 5)
		},
		{
			title: '',
			disabled: !(application.status === 'draft' &&
			application.current_step &&
			application.current_step >= 6)
		},
		{
			title: '',
			disabled: true
		}
	];

	const StepComponent = useMemo(() => {
		let component;
		switch (query.step) {
			case '0':
				component = ImportantNotesView;
				break;
			case '1':
				component = PersonalParticularsView;
				break;
			case '2':
				component = EducationBackgroundView;
				break;
			case '3':
				component = ApplicationInformationView;
				break;
			case '4':
				component = SupportingDocumentsView;
				break;
			case '5':
				component = InformationOfRefereesView;
				break;
			case '6':
				component = ReviewAndSubmissionView;
				break;
			case '7':
				component = ApplicationStatusView;
				break;
			default:
				component = ImportantNotesView;
				break;
		}

		return component;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query]);

	const handleResize = () => {
		setWindowWidth(window.innerWidth);
	};

	// Use Effects
	useEffect(() => {
		if (!query.step) {
			history.push('/application?step=0');
		}

		getPublishedEvent();

		// Add event listener for window resize
		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
			resetApplicationPage();
			resetApplicationStatusPage();
			resetImportantNotesPage();
			resetPersonalParticularPage();
			resetEducationBackgroundPage();
			resetApplicationInfoPage();
			resetDocumentPage();
			resetRefereeInfoPage();
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!query.application_id) {
			const user = LocalStorageHelper.getUser();
			if (user && user.application) {
				history.push(`/application?step=${query.step}&application_id=${user.application._id}`);
			}
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query]);

	useEffect(() => {
		query.application_id && getApplication(query.application_id);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query.step]);

	useEffect(() => {
		if(!_.isNil(selectedStep)){
			if(hasUnsavedChanges){
				setConfirmation(true);
			} else {
				history.push(`/application?step=${selectedStep}&application_id=${query.application_id}`);
				setSelectedStep(null);
			}
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedStep]);

	useEffect(() => {
		if (!_.isEmpty(application) && application) {
			// Do not allow vising previous step if application is already submitted
			if (application.status !== 'draft') {
				history.push(`/application?step=7&application_id=${application._id}`);
			}

			// Do not allow visiting other step in advance and visiting the first step
			// eslint-disable-next-line eqeqeq
			if (query.step > application.current_step) {
				history.push(`/application?step=${application.current_step}&application_id=${application._id}`);
			}
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [application]);

	if (loading) {
		return (
			<div
				style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
			>
				<Spin />
			</div>
		);
	}

	if (!event) {
		return (
			<div
				style={{
					backgroundColor: token.whiteColor,
					padding: '72px 20px',
					display: 'flex',
					flexDirection: 'column',
					borderRadius: 6
				}}
			>
				<Flex
					justify="center"
					align="center"
				>
					<Empty
						description="No Published Event"
					/>
				</Flex>
			</div>
		);
	}

	if (isMobileView) {
		return (
			<div
				style={{
					backgroundColor: token.whiteColor,
					padding: '30px 20px 0px 20px',
					display: 'flex',
					flexDirection: 'column',
					borderRadius: 6
				}}
			>
				<div
					style={{
						display: 'flex',
						flexDirection: 'row'
					}}
				>
					<div>
						<Steps
							current={+query.step}
							direction="vertical"
							labelPlacement="vertical"
							items={itemsWithOutTitle}
							size="small"
							style={{ height: '100%' }}
							onChange={(step) => {
								if(
									application.status === 'draft' &&
									application.current_step &&
									application.current_step >= Number(step)
								) {
									setSelectedStep(step);
								}
							}}
						/>
					</div>
					<div style={{ flex: 1 }}>
						<div style={{ margin: 25 }}>
							<StepComponent
								setHasUnsavedChanges={setHasUnsavedChanges}
								{...props}
							/>
						</div>
						<Confirmation
							open={confirmation}
							onCancel={() => {
								setConfirmation(false);
								setSelectedStep(null);
							}}
							onOk={() => {
								history.push(`/application?step=${selectedStep}&application_id=${query.application_id}`);
								setConfirmation(false);
								setSelectedStep(null);
							}}
						>
							<Flex
								vertical
								gap={30}
							>
								<Text>
									Navigating away from this step will discard unsaved changes.
									Please click on Save as Draft to save your changes before you proceed.
								</Text>
								<Text>
									{`Are you sure you want to go to step ${selectedStep + 1}?`}
								</Text>
							</Flex>
						</Confirmation>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div
			style={{
				backgroundColor: token.whiteColor,
				padding: '72px 20px 0px',
				display: 'flex',
				flexDirection: 'column',
				borderRadius: 6
			}}
		>
			<div style={{ maxWidth: 1230, width: '100%', alignSelf: 'center', padding: '0px 20px' }}>
				<Steps
					current={+query.step}
					labelPlacement="vertical"
					items={itemsWithTitle}
					style={{ width: '100%' }}
					onChange={(step) => {
						if(
							application.status === 'draft' &&
							application.current_step &&
							application.current_step >= Number(step)
						) {
							setSelectedStep(step);
						}
					}}
				/>
				<div style={{ margin: '66px 25px 25px' }}>
					<StepComponent
						setHasUnsavedChanges={setHasUnsavedChanges}
						{...props}
					/>
				</div>
				<Confirmation
					open={confirmation}
					onCancel={() => {
						setConfirmation(false);
						setSelectedStep(null);
					}}
					onOk={() => {
						history.push(`/application?step=${selectedStep}&application_id=${query.application_id}`);
						setConfirmation(false);
						setSelectedStep(null);
					}}
				>
					<Flex
						vertical
						gap={30}
					>
						<Text>
							Navigating away from this step will discard unsaved changes.
							Please click on Save as Draft to save your changes before you proceed.
						</Text>
						<Text>
							{`Are you sure you want to go to step ${selectedStep + 1}?`}
						</Text>
					</Flex>
				</Confirmation>
			</div>
		</div>
	);
};

export default ApplicationPage;